import React, { useEffect, useState } from 'react';

interface AuditEntry {
  id: number;
  actor_id: number | null;
  actor_email: string | null;
  action: string;
  table_name: string;
  record_id: number | null;
  details: object | null;
  ip_address: string | null;
  timestamp: string;
}

const getLastWeekRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const actionLabels: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  login: 'Login',
  login_success: 'Login Success',
  password_reset: 'Password Reset',
  password_change: 'Password Change',
  sport_membership_update: 'Sport Membership Update',
  sport_role_update: 'Sport Role Update',
};

const tableLabels: Record<string, string> = {
  member: 'Member',
  members: 'Member',
  sport: 'Sport',
  sports: 'Sport',
  sport_roles: 'Sport Role',
  sport_member_roles: 'Sport Member Role',
  sport_members: 'Sport Membership',
  grades: 'Grade',
  system_settings: 'System Setting',
};

const actionColors: Record<string, string> = {
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-yellow-100 text-yellow-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-green-100 text-green-800',
  login_success: 'bg-green-100 text-green-800',
  password_reset: 'bg-orange-100 text-orange-800',
  password_change: 'bg-orange-100 text-orange-800',
  sport_membership_update: 'bg-purple-100 text-purple-800',
  sport_role_update: 'bg-purple-100 text-purple-800',
};

interface AuditLogScreenProps {
  apiBaseUrl: string;
  accessToken: string | null;
}

const AuditLogScreen: React.FC<AuditLogScreenProps> = ({ apiBaseUrl, accessToken }) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(getLastWeekRange());
  const [queried, setQueried] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState('');
  const [filterTable, setFilterTable] = useState('');

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('start', dateRange.start);
      params.set('end', dateRange.end);
      if (filterAction) params.set('action', filterAction);
      if (filterTable) params.set('table_name', filterTable);

      const res = await fetch(`${apiBaseUrl}/auditlog?${params.toString()}`, {
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setEntries([]);
        setQueryError(errData.message || `Request failed (${res.status})`);
        setQueried(true);
        return;
      }
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
      setQueryError(null);
      setQueried(true);
    } catch (err) {
      setEntries([]);
      setQueryError('Network error');
      setQueried(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudit();
  }, [dateRange, filterAction, filterTable]);

  const detailsToString = (details: object | null): string => {
    if (!details) return '';
    return JSON.stringify(details);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Audit Log</h2>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Start Date:</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
            className="border rounded px-3 py-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">End Date:</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
            className="border rounded px-3 py-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Action:</label>
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="border rounded px-3 py-1"
          >
            <option value="">All</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="password_reset">Password Reset</option>
            <option value="password_change">Password Change</option>
            <option value="login">Login</option>
            <option value="sport_membership_update">Sport Membership</option>
            <option value="sport_role_update">Sport Role</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Table:</label>
          <select
            value={filterTable}
            onChange={e => setFilterTable(e.target.value)}
            className="border rounded px-3 py-1"
          >
            <option value="">All</option>
            <option value="member">Member</option>
            <option value="sport">Sport</option>
            <option value="sport_roles">Sport Role</option>
            <option value="sport_members">Sport Membership</option>
            <option value="grades">Grade</option>
            <option value="system_settings">System Setting</option>
          </select>
        </div>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={fetchAudit}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Error */}
      {queried && !loading && queryError && (
        <div className="text-red-500 mb-4">{queryError}</div>
      )}

      {/* Empty state */}
      {queried && !loading && entries.length === 0 && (
        <div className="text-gray-500">No audit log records found for this period.</div>
      )}

      {/* Table */}
      {loading ? (
        <div>Loading...</div>
      ) : entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="border px-3 py-2 text-left">Timestamp</th>
                <th className="border px-3 py-2 text-left">Actor</th>
                <th className="border px-3 py-2 text-left">Action</th>
                <th className="border px-3 py-2 text-left">Table</th>
                <th className="border px-3 py-2 text-left">Record ID</th>
                <th className="border px-3 py-2 text-left">Details</th>
                <th className="border px-3 py-2 text-left">IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const colorClass = actionColors[e.action] || 'bg-gray-100 text-gray-800';
                return (
                  <tr key={e.id} className={colorClass.replace(/bg-\w+-100/, 'hover:bg-opacity-50')}>
                    <td className="border px-3 py-2 whitespace-nowrap">{e.timestamp}</td>
                    <td className="border px-3 py-2">
                      {e.actor_email || 'Unknown'}
                      {e.actor_id ? ` (ID: ${e.actor_id})` : ''}
                    </td>
                    <td className="border px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
                        {actionLabels[e.action] || e.action}
                      </span>
                    </td>
                    <td className="border px-3 py-2">{tableLabels[e.table_name] || e.table_name}</td>
                    <td className="border px-3 py-2">{e.record_id || '-'}</td>
                    <td className="border px-3 py-2 max-w-xs truncate" title={detailsToString(e.details)}>
                      {detailsToString(e.details)}
                    </td>
                    <td className="border px-3 py-2 text-xs">{e.ip_address || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogScreen;
